import React, { useState, useEffect } from 'react';
import Header from '../components/ui/Header';
import AuthenticationGuard from '../components/ui/AuthenticationGuard';
import Icon from '../components/AppIcon';
import Button from '../components/ui/Button';
import Input from '../components/ui/Input';

const HelpPage = () => {
  const [faqs, setFaqs] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedFaq, setExpandedFaq] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('All');

  // Mock FAQ data
  const mockFaqs = [
    {
      id: 1,
      question: 'How do I create a new habit?',
      answer: 'To create a new habit, go to the Habits page and click the "Add Habit" button. Fill in the habit name, description, category, and frequency. You can also set reminders and choose an icon.',
      category: 'Habits',
      tags: ['habits', 'create', 'new']
    },
    {
      id: 2,
      question: 'How do flashcards work?',
      answer: 'Flashcards use spaced repetition to help you learn efficiently. Create decks of cards with questions and answers. The system will show you cards based on how well you know them, presenting difficult cards more frequently.',
      category: 'Flashcards',
      tags: ['flashcards', 'spaced repetition', 'learning']
    },
    {
      id: 3,
      question: 'Can I export my data?',
      answer: 'Yes! You can export your data from the Settings page under Privacy settings. Choose your export frequency (monthly, quarterly, or yearly) and you\'ll receive a download link via email.',
      category: 'Account',
      tags: ['export', 'data', 'backup']
    },
    {
      id: 4,
      question: 'How are streaks calculated?',
      answer: 'Streaks count consecutive days you complete a habit. Missing a day resets the streak to 0. Your best streak is always saved so you can see your personal record.',
      category: 'Progress',
      tags: ['streaks', 'progress', 'tracking']
    },
    {
      id: 5,
      question: 'What is the AI Coach feature?',
      answer: 'The AI Coach provides personalized recommendations based on your learning patterns. It suggests optimal study times, identifies areas for improvement, and helps you stay motivated.',
      category: 'Features',
      tags: ['ai', 'coach', 'recommendations']
    },
    {
      id: 6,
      question: 'How do I reset my password?',
      answer: 'Click "Forgot Password" on the login page and enter your email address. You\'ll receive a reset link within a few minutes. If you don\'t see it, check your spam folder.',
      category: 'Account',
      tags: ['password', 'reset', 'login']
    },
    {
      id: 7,
      question: 'Can I use Cogito offline?',
      answer: 'Cogito works best with an internet connection for syncing data. However, you can view previously loaded content offline. Changes made offline will sync when you reconnect.',
      category: 'Technical',
      tags: ['offline', 'sync', 'connection']
    },
    {
      id: 8,
      question: 'How do I change my notification settings?',
      answer: 'Go to Settings > Notifications to customize when and how you receive reminders. You can enable/disable habit reminders, study alerts, achievement notifications, and weekly reports.',
      category: 'Settings',
      tags: ['notifications', 'settings', 'reminders']
    }
  ];

  const categories = ['All', 'Habits', 'Flashcards', 'Account', 'Progress', 'Features', 'Technical', 'Settings'];

  useEffect(() => {
    // Simulate loading FAQ data
    const loadFaqs = async () => {
      try {
        await new Promise(resolve => setTimeout(resolve, 800));
        setFaqs(mockFaqs);
        setIsLoading(false);
      } catch (error) {
        console.error('Error loading FAQs:', error);
        setIsLoading(false);
      }
    };

    loadFaqs();
  }, []);

  const filteredFaqs = faqs?.filter(faq => {
    const matchesSearch = faq?.question?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
                         faq?.answer?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
                         faq?.tags?.some(tag => tag?.toLowerCase()?.includes(searchTerm?.toLowerCase()));
    const matchesCategory = selectedCategory === 'All' || faq?.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const toggleFaq = (faqId) => {
    setExpandedFaq(expandedFaq === faqId ? null : faqId);
  };

  if (isLoading) {
    return (
      <AuthenticationGuard isAuthenticated={true}>
        <div className="min-h-screen bg-background">
          <Header />
          <div className="pt-16">
            <div className="max-w-4xl mx-auto px-4 lg:px-6 py-8">
              <div className="flex items-center justify-center min-h-[60vh]">
                <div className="text-center space-y-4">
                  <div className="w-16 h-16 bg-primary/10 rounded-xl flex items-center justify-center mx-auto animate-pulse">
                    <div className="w-8 h-8 bg-primary rounded-lg"></div>
                  </div>
                  <div className="space-y-2">
                    <div className="h-6 bg-muted rounded w-48 mx-auto animate-pulse"></div>
                    <div className="h-4 bg-muted rounded w-32 mx-auto animate-pulse"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </AuthenticationGuard>
    );
  }

  return (
    <AuthenticationGuard isAuthenticated={true}>
      <div className="min-h-screen bg-background">
        <Header />
        <main className="pt-16">
          <div className="max-w-4xl mx-auto px-4 lg:px-6 py-8">
            {/* Page Header */}
            <div className="mb-8 text-center">
              <h1 className="text-3xl font-bold text-foreground mb-2">Help Center</h1>
              <p className="text-muted-foreground">Find answers to common questions about Cogito</p>
            </div>

            {/* Search */}
            <div className="mb-8">
              <div className="relative">
                <Icon 
                  name="Search" 
                  size={20} 
                  className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" 
                />
                <Input
                  type="text"
                  placeholder="Search for help articles..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e?.target?.value)}
                  className="pl-10"
                />
              </div>
            </div>

            {/* Categories */}
            <div className="mb-8">
              <div className="flex flex-wrap gap-2">
                {categories?.map((category) => (
                  <button
                    key={category}
                    onClick={() => setSelectedCategory(category)}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      selectedCategory === category
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-muted text-muted-foreground hover:bg-muted/80'
                    }`}
                  >
                    {category}
                  </button>
                ))}
              </div>
            </div>

            {/* Quick Actions */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              <div className="bg-card rounded-xl border border-border p-6 text-center">
                <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mx-auto mb-4">
                  <Icon name="MessageCircle" size={24} className="text-primary" />
                </div>
                <h3 className="font-semibold text-foreground mb-2">Contact Support</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Get personalized help from our support team
                </p>
                <Button variant="outline" size="sm">
                  Send Message
                </Button>
              </div>

              <div className="bg-card rounded-xl border border-border p-6 text-center">
                <div className="w-12 h-12 bg-success/10 rounded-lg flex items-center justify-center mx-auto mb-4">
                  <Icon name="BookOpen" size={24} className="text-success" />
                </div>
                <h3 className="font-semibold text-foreground mb-2">User Guide</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Complete guide to using all Cogito features
                </p>
                <Button variant="outline" size="sm">
                  Read Guide
                </Button>
              </div>

              <div className="bg-card rounded-xl border border-border p-6 text-center">
                <div className="w-12 h-12 bg-accent/10 rounded-lg flex items-center justify-center mx-auto mb-4">
                  <Icon name="Video" size={24} className="text-accent" />
                </div>
                <h3 className="font-semibold text-foreground mb-2">Video Tutorials</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Step-by-step video guides and walkthroughs
                </p>
                <Button variant="outline" size="sm">
                  Watch Videos
                </Button>
              </div>
            </div>

            {/* FAQ Section */}
            <div className="bg-card rounded-xl border border-border p-6">
              <h2 className="text-xl font-semibold text-foreground mb-6">
                Frequently Asked Questions
              </h2>

              <div className="space-y-4">
                {filteredFaqs?.map((faq) => (
                  <div
                    key={faq?.id}
                    className="border border-border rounded-lg overflow-hidden"
                  >
                    <button
                      onClick={() => toggleFaq(faq?.id)}
                      className="w-full p-4 text-left bg-muted/30 hover:bg-muted/50 transition-colors flex items-center justify-between"
                    >
                      <div className="flex-1">
                        <h3 className="font-medium text-foreground">
                          {faq?.question}
                        </h3>
                        <div className="flex items-center space-x-2 mt-1">
                          <span className="text-xs bg-primary/10 text-primary px-2 py-1 rounded-full">
                            {faq?.category}
                          </span>
                        </div>
                      </div>
                      <Icon 
                        name={expandedFaq === faq?.id ? "ChevronUp" : "ChevronDown"} 
                        size={20} 
                        className="text-muted-foreground" 
                      />
                    </button>
                    
                    {expandedFaq === faq?.id && (
                      <div className="p-4 bg-card border-t border-border">
                        <p className="text-muted-foreground leading-relaxed">
                          {faq?.answer}
                        </p>
                        {faq?.tags?.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-3">
                            {faq?.tags?.map((tag, index) => (
                              <span
                                key={index}
                                className="text-xs bg-muted px-2 py-1 rounded-full text-muted-foreground"
                              >
                                {tag}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {filteredFaqs?.length === 0 && (
                <div className="text-center py-12">
                  <div className="w-16 h-16 bg-muted/30 rounded-xl flex items-center justify-center mx-auto mb-4">
                    <Icon name="Search" size={32} className="text-muted-foreground" />
                  </div>
                  <p className="text-lg font-medium text-foreground mb-2">No results found</p>
                  <p className="text-muted-foreground">
                    Try different keywords or contact support for help
                  </p>
                </div>
              )}
            </div>

            {/* Contact Section */}
            <div className="mt-8 bg-card rounded-xl border border-border p-6 text-center">
              <h3 className="text-lg font-semibold text-foreground mb-2">
                Still need help?
              </h3>
              <p className="text-muted-foreground mb-4">
                Can't find what you're looking for? Our support team is here to help.
              </p>
              <div className="flex items-center justify-center space-x-4">
                <Button>
                  <Icon name="Mail" size={16} className="mr-2" />
                  Email Support
                </Button>
                <Button variant="outline">
                  <Icon name="MessageCircle" size={16} className="mr-2" />
                  Live Chat
                </Button>
              </div>
            </div>
          </div>
        </main>
      </div>
    </AuthenticationGuard>
  );
};

export default HelpPage;